import { API_CONFIG } from "./apiConfig";
import { networkEventManager } from "./networkEventManager";
import { IContractPreview, IPolicyPreview } from "../types/chat";
import {
  AllFilters,
  Claim,
  ClaimDetailResponse,
  ClaimsDashboardStatistics,
  ClaimsStatistics,
  ClaimsSummary,
  ClaimsUploadResult,
  DailyStatsResponse,
  FileListResponse,
  FileHistoryDetail,
  ClaimFilterView,
  SavedClaimViewFilters,
  ReprocessFilesResponse,
  FeeChange,
  RecordAppealInput,
  RecordAppealResponse,
  RecordManualPaymentInput,
  RecordManualPaymentResponse,
  RemoveManualPaymentResponse,
  JoinManualPaymentResponse,
  ClaimLink,
  ClaimLinkCandidatesQuery,
  ClaimLinkCandidatesResponse,
  LinkClaimInput,
  ClaimLinkPreview,
} from "../types/claims";
import { SelectOption } from "../components/ui/core/CoreSelect";
import { ContactSupportFormType } from "../config/constants";
import {
  CasesBreakdown,
  ClaimOutcomeTracking,
  DrillDownCapability,
  DrillDownCapabilityLineItemRow,
  FinancialImpact,
  SuccessFeesPerClaimResponse,
  UsageActivityFeedColumnFilters,
  UsageActivityFeedResponse,
  UsageByAgent,
  UsageKpis,
} from "../types/observability";
import type { ColumnFilterEntry } from "../types/claims";
import type { ColumnFilterState } from "../types/table";
import type { ObservabilityDateRange } from "../components/helpers/dateIntervalHelper";
import type {
  ReleaseVersion,
  ReleaseVersionsResponse,
} from "../types/releaseNotes";
import type {
  ITicket,
  ITicketDetail,
  ITicketListResponse,
  TicketColumnFilters,
} from "../types/tickets";
// Base API configuration
const API_BASE_URL = API_CONFIG.BASE_URL;

// Splits files larger than MAX_FILE_SIZE into chunks and sends them sequentially.
const UPLOAD_CHUNK_SIZE = API_CONFIG.UPLOAD_LIMITS.MAX_FILE_SIZE;

// API response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  status?: number;
}

export type AppealLetterFormat = "docx" | "pdf";

/** A document rendered by the server, with the filename the server chose. */
export interface DocumentDownload {
  blob: Blob;
  filename: string | null;
}

/**
 * Filename out of a Content-Disposition header, or null when there is none.
 *
 * The server names these documents (claim number where there is one, chat id
 * otherwise), so the browser should use that name rather than invent a second
 * convention. RFC 5987's `filename*` is tried first, then the plain form.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Malformed percent-encoding; fall through to the plain form.
    }
  }

  const plain = header.match(/filename="?([^"';]+)"?/i);

  return plain ? plain[1].trim() : null;
}

export interface PaginatedApiResponse<T = any> {
  success: boolean;
  data?: {
    results: T[];
    count?: number;
    next?: string | null;
    previous?: string | null;
  };
  message?: string;
  error?: string;
  status?: number;
}

export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "assistant" | "status";
  timestamp: string;
  attachments?: FileAttachment[];
}

export interface FileAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  is_pinned?: boolean;
  isPinned?: boolean;
  metadata?: Record<string, any>;
  tags?: string[];
  experiments?: Record<string, boolean>;
  claim_number?: string;
  user_name?: string;
  user_email?: string;
  shared?: boolean;
  closed_at?: string | null;
  status?: "open" | "closed";
  chat_ref?: string | null;
  files?: any[];
  files_count?: number;
}

export interface ChatSearchResult {
  chat_id: string;
  chat_title: string;
  claim_number?: string;
  user_name?: string;
  user_email?: string;
  shared?: boolean;
  closed_at?: string | null;
  message: {
    id: string;
    content: string;
    role: string;
    created_at: string;
  };
  highlights: Array<{
    start: number;
    end: number;
  }>;
}

export interface ChatSearchResponse {
  query: string;
  results: ChatSearchResult[];
  total_results: number;
}

export interface FileUploadResponse {
  message: string;
  file_url: string;
  filename: string;
  file_id: string;
  is_edi?: boolean;
  is_processed?: boolean;
  processing_error?: string;
  upload_id?: string; // Received when uploading files in chunks
  ticket?: ITicket;
  timestamp?: string;
  // Present when an EDI upload was ingested into claims (same engine as the claims-table upload)
  claim_ingest?: ClaimIngestResult;
}

export interface ClaimIngestResult {
  ingested: boolean;
  error?: boolean;
  claims_created?: number;
  claims_updated?: number;
  is_duplicate?: boolean;
  claim_ids?: string[];
  // Set when the upload came from a chat tied to a claim: `matched` means the EDI
  // covers that claim (file is shown in the claim viewer, not as a chat attachment);
  // `mismatch` is a non-blocking warning that the EDI belongs to different claim(s).
  matched?: boolean;
  mismatch?: boolean;
  chat_claim_number?: string;
  edi_claim_numbers?: string[];
}

export interface Payer {
  code: string;
  name: string;
  payers: { lines_of_business: string[]; name: string }[];
}

export interface ApiKeySettingItem {
  id: string;
  prefix: string;
  tenants: string[];
  last_used: string | null;
  status: string;
  ip_allowlist: string[];
}

export interface ApiKeySetting {
  api_keys: ApiKeySettingItem[];
}

// Returned only from the rotate endpoint: `secret` is the full key and is never sent again.
export interface ApiKeyRotateResponse extends ApiKeySetting {
  secret: string;
}

class ApiService {
  private baseUrl: string;
  private headers: Record<string, string>;
  private activeChatId: string | null = null;
  private activeClaimNumber: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  public setActiveChatId(chatId: string | null) {
    this.activeChatId = chatId;
  }

  public setActiveClaimNumber(claimNumber: string | null) {
    this.activeClaimNumber = claimNumber;
  }

  private uploadTraceHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.activeChatId) {
      headers["X-Chat-Id"] = this.activeChatId;
    } else if (this.activeClaimNumber) {
      // Draft claim chat (Ask Reve) before the chat record exists
      headers["X-Claim-Number"] = this.activeClaimNumber;
    }
    return headers;
  }

  private extractErrorMessage(data: unknown, status: number): string {
    if (data && typeof data === "object") {
      const body = data as Record<string, unknown>;
      if (typeof body.message === "string" && body.message.trim()) {
        return body.message;
      }
      if (typeof body.error === "string" && body.error.trim()) {
        return body.error;
      }
    }
    return `HTTP ${status}`;
  }

  public async request<T>(
    endpoint: string,
    options: RequestInit = {},
    baseUrl: string = this.baseUrl,
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${baseUrl}${endpoint}`;
      const traceHeaders: Record<string, string> = {};
      if (this.activeChatId) {
        traceHeaders["X-Chat-Id"] = this.activeChatId;
      }
      const config: RequestInit = {
        headers: { ...this.headers, ...traceHeaders },
        credentials: "include",
        ...options,
      };

      const response = await fetch(url, config);

      // Handle empty responses (like 204 No Content for DELETE requests)
      let data: any = null;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // If JSON parsing fails, continue with null data
        }
      }

      if (!response.ok) {
        // 401 = unauthenticated session. 403/409 are permission/business-rule errors.
        if (response.status === 401) {
          networkEventManager.emitAuthFailure(
            `API auth failure: ${response.status}`,
            { endpoint, status: response.status },
          );
        } else if (response.status >= 500) {
          // Server errors - emit network error
          networkEventManager.emitNetworkError(
            `Server error: ${response.status}`,
            { endpoint, status: response.status },
          );
        }

        return {
          success: false,
          error: this.extractErrorMessage(data, response.status),
          status: response.status,
          data: data, // Include parsed data even on error responses for detailed error handling
        };
      }

      return {
        success: true,
        data,
        status: response.status,
      };
    } catch (error) {
      // Don't emit errors for aborted requests (user switched chats)
      if (error instanceof Error && error.name === "AbortError") {
        return {
          success: false,
          error: "Request aborted",
          status: 0,
        };
      }
      // Emit network error event for network failures
      networkEventManager.emitNetworkError("API request failed", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  // Chat Management
  async createChat(
    title?: string,
    tag?: string,
    claimId?: string,
    experiments?: Record<string, boolean>,
    initialAssistantMessage?: string,
  ): Promise<ApiResponse<ChatSession>> {
    const body: any = { title: title || "", tags: tag ? [tag] : [] };
    if (experiments && Object.keys(experiments).length > 0) {
      body.experiments = experiments;
    }
    if (claimId) {
      body.claim_number = claimId;
    }
    if (initialAssistantMessage) {
      body.initial_assistant_message = initialAssistantMessage;
    }
    return this.request<ChatSession>(API_CONFIG.ENDPOINTS.CHATS, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async getChat(
    chatId: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<ChatSession>> {
    return this.request<ChatSession>(`/chats/${chatId}/`, { signal });
  }

  async pollChatMessages(
    chatId: string,
    since: string,
    signal?: AbortSignal,
  ): Promise<
    ApiResponse<{ messages: ChatMessage[]; is_processing: boolean | null }>
  > {
    const query = `?since=${encodeURIComponent(since)}`;
    try {
      const url = `${this.baseUrl}/chats/${chatId}/messages/${query}`;
      const response = await fetch(url, {
        headers: this.headers,
        credentials: "include",
        signal,
      });
      if (!response.ok)
        return {
          success: false,
          error: `HTTP ${response.status}`,
          status: response.status,
        };
      const messages = await response.json();
      // '1' / '0' = lock held / released; 'unknown' or missing = null (no opinion).
      const processingHeader = response.headers.get("X-Chat-Processing");
      const isProcessing =
        processingHeader === "1"
          ? true
          : processingHeader === "0"
            ? false
            : null;
      return {
        success: true,
        data: { messages, is_processing: isProcessing },
        status: response.status,
      };
    } catch {
      return { success: false, error: "Network error" };
    }
  }

  async getChats(
    page?: number,
    claimId?: string,
  ): Promise<PaginatedApiResponse<ChatSession>> {
    const params = new URLSearchParams();
    if (page) params.append("page", page.toString());
    if (claimId) {
      params.append("claim_number", claimId);
      // Bring chats that are not pinned. Pinned chats are loaded separately.
      params.append("pinned", "false");
    }
    const queryString = params.toString() ? `?${params.toString()}` : "";
    return this.request<{
      results: ChatSession[];
      count?: number;
      next?: string | null;
      previous?: string | null;
    }>(`/chats/${queryString}`);
  }

  async searchChats(
    q: string,
    limit?: number,
    messageId?: string,
    signal?: AbortSignal,
  ): Promise<ApiResponse<ChatSearchResponse>> {
    const params = new URLSearchParams();
    params.append("q", q);

    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }

    if (messageId) {
      params.append("message_id", messageId);
    }

    const queryString = params.toString();
    return this.request<ChatSearchResponse>(`/chats/search/?${queryString}`, {
      signal,
    });
  }

  async deleteChat(chatId: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.request<void>(`/chats/${chatId}/`, {
        method: "DELETE",
      });

      // Consider 204 No Content as success for DELETE operations
      if (response.status === 204) {
        return {
          success: true,
          data: undefined,
          status: 204,
        };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to delete chat",
      };
    }
  }

  // Update chat title
  async updateChatTitle(
    chatId: string,
    title: string,
    isPinned?: boolean,
  ): Promise<ApiResponse<ChatSession>> {
    const body: any = { title: title };
    if (isPinned !== undefined) {
      body.is_pinned = isPinned;
    }
    return this.request<ChatSession>(`/chats/${chatId}/`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  // Update a chat's experiment switches (REV-2161)
  async updateChatExperiments(
    chatId: string,
    experiments: Record<string, boolean>,
  ): Promise<ApiResponse<ChatSession>> {
    return this.request<ChatSession>(`/chats/${chatId}/`, {
      method: "PATCH",
      body: JSON.stringify({ experiments }),
    });
  }

  // Close a chat (terminal, irreversible). Idempotent on the backend.
  async closeChat(chatId: string): Promise<ApiResponse<ChatSession>> {
    return this.request<ChatSession>(`/chats/${chatId}/close/`, {
      method: "POST",
    });
  }

  // Clone a chat completely (full history, files, claim, context). The source
  // may be open or closed; the returned chat is a new, independent, open chat.
  async cloneChat(chatId: string): Promise<ApiResponse<ChatSession>> {
    return this.request<ChatSession>(`/chats/${chatId}/clone/`, {
      method: "POST",
    });
  }

  /** Persist chat list order. Backend expects { chat_ids: string[] } in the order to display. */
  async reorderChats(chatIds: string[]): Promise<ApiResponse<void>> {
    return this.request<void>(API_CONFIG.ENDPOINTS.CHATS_REORDER, {
      method: "POST",
      body: JSON.stringify({ chat_ids: chatIds }),
    });
  }

  async sendMessage(
    chatId: string,
    content: string,
    attachments?: File[],
  ): Promise<ApiResponse<ChatMessage>> {
    const formData = new FormData();
    formData.append("content", content);

    if (attachments) {
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
    }

    return this.request<ChatMessage>(
      API_CONFIG.ENDPOINTS.CHAT_MESSAGES(chatId),
      {
        method: "GET",
        body: formData,
      },
    );
  }

  // File Upload and Processing (streamed by browser: no full file in memory)
  async uploadFile(file: File): Promise<ApiResponse<FileUploadResponse>> {
    if (file.size <= UPLOAD_CHUNK_SIZE) {
      const singleUploadHeaders: Record<string, string> = {
        ...this.headers,
        "Content-Type": "application/octet-stream",
        "x-filename": encodeURIComponent(file.name),
        ...this.uploadTraceHeaders(),
      };
      return this.request<FileUploadResponse>("/files/upload/", {
        method: "POST",
        headers: singleUploadHeaders,
        body: file,
      });
    }

    // Send file in chunks
    const totalChunks = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);
    let lastResponse: ApiResponse<FileUploadResponse> | null = null;

    let uploadRefId = null as null | string;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * UPLOAD_CHUNK_SIZE;
      const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
      const chunkBlob = file.slice(start, end);

      const uploadHeaders: Record<string, string> = {
        ...this.headers,
        "Content-Type": "application/octet-stream",
        "x-filename": encodeURIComponent(file.name),
        "x-chunk-index": String(chunkIndex),
        "x-total-chunks": String(totalChunks),
        "x-total-size": String(file.size),
        "x-chunk-size": String(UPLOAD_CHUNK_SIZE),
        ...this.uploadTraceHeaders(),
      };
      if (uploadRefId) uploadHeaders["x-upload-id"] = uploadRefId;

      const response = await this.request<FileUploadResponse>(
        "/files/upload-chunked/",
        {
          method: "POST",
          headers: uploadHeaders,
          body: chunkBlob,
        },
      );

      if (response?.data?.upload_id) {
        uploadRefId = response?.data?.upload_id;
      }

      if (!response.success) {
        return response;
      }

      lastResponse = response;
    }

    return lastResponse ?? { success: false, error: "No chunks were uploaded" };
  }

  async downloadFile(
    fileId: string,
    inline: boolean = false,
    parsed: boolean = false,
  ): Promise<ApiResponse<Blob>> {
    const params = new URLSearchParams();
    params.append("inline", inline.toString());
    if (parsed) {
      params.append("parsed", "true");
    }

    const queryString = params.toString();
    const endpoint = `${API_CONFIG.ENDPOINTS.FILE_DOWNLOAD(fileId)}?${queryString}`;

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const config: RequestInit = {
        headers: this.headers,
        credentials: "include",
        method: "GET",
      };

      const response = await fetch(url, config);

      if (!response.ok) {
        return {
          success: false,
          error: `Failed to download file: ${response.status} ${response.statusText}`,
          status: response.status,
        };
      }

      const blob = await response.blob();

      return {
        success: true,
        data: blob,
        status: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  /**
   * The appeal letter for a chat, rendered by the backend (REV-2047).
   *
   * One route serves both formats — `?format=` picks the renderer — so what the
   * user downloads is a render of what the backend stores, rather than a second
   * rendering of the editor's DOM that has to be kept in step with it.
   */
  async downloadAppealLetter(
    chatId: string,
    format: AppealLetterFormat = "docx",
    version?: number,
  ): Promise<ApiResponse<DocumentDownload>> {
    const path = API_CONFIG.ENDPOINTS.CHAT_OUTPUT_DOCUMENT(chatId, version ?? "latest");

    // Always explicit: without `format` this endpoint answers JSON.
    return this.downloadDocument(`${path}?format=${format}`);
  }

  /**
   * GET a binary document, keeping the server's own error wording.
   *
   * Unlike request(), a successful body must not be parsed as JSON — it is the
   * document. On failure the body *is* JSON (`{"error": ...}`) and its message is
   * worth showing: "no appeal letter yet" and "still being generated" ask
   * different things of the user, and a bare status code says neither.
   */
  private async downloadDocument(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<DocumentDownload>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "GET",
        headers: { Accept: "*/*" },
        credentials: "include",
        ...options,
      });

      if (!response.ok) {
        let body: unknown = null;
        try {
          body = await response.json();
        } catch {
          // A gateway/proxy error page: the status is all there is to report.
        }

        if (response.status === 401) {
          networkEventManager.emitAuthFailure(
            `API auth failure: ${response.status}`,
            { endpoint, status: response.status },
          );
        } else if (response.status >= 500) {
          networkEventManager.emitNetworkError(
            `Server error: ${response.status}`,
            { endpoint, status: response.status },
          );
        }

        return {
          success: false,
          error: this.extractErrorMessage(body, response.status),
          status: response.status,
        };
      }

      return {
        success: true,
        data: {
          blob: await response.blob(),
          filename: filenameFromDisposition(
            response.headers.get("content-disposition"),
          ),
        },
        status: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  async getLetterFormat(): Promise<ApiResponse<any>> {
    try {
      const url = `${this.baseUrl}${API_CONFIG.ENDPOINTS.LETTER_FORMAT}`;
      const config: RequestInit = {
        headers: this.headers,
        credentials: "include",
        method: "GET",
      };

      const response = await fetch(url, config);

      if (!response.ok) {
        return {
          success: false,
          error: `Failed to get letter format: ${response.status} ${response.statusText}`,
          status: response.status,
        };
      }

      const data = await response.json();

      return {
        success: true,
        data: data,
        status: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  // Api Key
  async getApiKeySetting(): Promise<ApiResponse<ApiKeySetting>> {
    return this.request<ApiKeySetting>(API_CONFIG.ENDPOINTS.API_KEY);
  }

  async rotateApiKey(id: string): Promise<ApiResponse<ApiKeyRotateResponse>> {
    return this.request<ApiKeyRotateResponse>(
      API_CONFIG.ENDPOINTS.API_KEY_ROTATE(id),
      {
        method: "POST",
      },
    );
  }

  async updateApiKeyAllowlist(
    id: string,
    ipAllowlist: string[],
  ): Promise<ApiResponse<ApiKeySetting>> {
    return this.request<ApiKeySetting>(
      API_CONFIG.ENDPOINTS.API_KEY_ALLOWLIST(id),
      {
        method: "PUT",
        body: JSON.stringify({ ip_allowlist: ipAllowlist }),
      },
    );
  }

  // Payers
  async getPayers(): Promise<ApiResponse<Payer[]>> {
    return this.request<Payer[]>(API_CONFIG.ENDPOINTS.PAYERS);
  }

  async getContractPayers(): Promise<ApiResponse<Payer[]>> {
    return this.request<Payer[]>(API_CONFIG.ENDPOINTS.CONTRACT_PAYERS());
  }

  async getTenantContracts(
    payer: string,
    state?: string,
    lineOfBusiness?: string,
    start_after_id?: string,
    start_after_sort_value?: string,
    search?: string,
    sort_by?: string,
    sort_direction?: string,
    columnFilters?: Record<string, ColumnFilterState>,
    signal?: AbortSignal,
  ): Promise<ApiResponse<IContractPreview>> {
    return this.request<IContractPreview>(
      API_CONFIG.ENDPOINTS.CONTRACTS_BY_TENANT(
        payer,
        state,
        lineOfBusiness,
        start_after_id,
        start_after_sort_value,
        search,
        sort_by,
        sort_direction,
        columnFilters,
      ),
      { signal },
    );
  }

  async getTenantPolicies(
    payer?: string,
    state?: string,
    lineOfBusiness?: string,
    start_after_id?: string,
    start_after_sort_value?: string,
    search?: string,
    sort_by?: string,
    sort_direction?: string,
    columnFilters?: Record<string, ColumnFilterState>,
    signal?: AbortSignal,
  ): Promise<ApiResponse<IPolicyPreview>> {
    return this.request<IPolicyPreview>(
      API_CONFIG.ENDPOINTS.POLICIES_BY_TENANT(
        payer,
        state,
        lineOfBusiness,
        start_after_id,
        start_after_sort_value,
        search,
        sort_by,
        sort_direction,
        columnFilters,
      ),
      { signal },
    );
  }

  async downloadDocumentById(documentId: string): Promise<ApiResponse<Blob>> {
    const endpoint = API_CONFIG.ENDPOINTS.DOCUMENT_BY_ID_DOWNLOAD(documentId);

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const config: RequestInit = {
        headers: this.headers,
        credentials: "include",
        method: "GET",
      };

      const response = await fetch(url, config);

      if (!response.ok) {
        return {
          success: false,
          error: `Failed to download document: ${response.status} ${response.statusText}`,
          status: response.status,
        };
      }

      const blob = await response.blob();
      return {
        success: true,
        data: blob,
        status: response.status,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  // Claims
  async getStatisticsData(): Promise<ClaimsStatistics> {
    const response = await this.request<ClaimsStatistics>(
      API_CONFIG.ENDPOINTS.CLAIMS_STATISTICS,
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Fail to extract statistics data");
  }

  async getDashboardStatistics(): Promise<ClaimsDashboardStatistics> {
    const response = await this.request<ClaimsDashboardStatistics>(
      API_CONFIG.ENDPOINTS.CLAIMS_DASHBOARD_STATISTICS,
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Failed to load dashboard statistics");
  }

  async getClaimsData(
    page?: number,
    pageSize?: number,
    orderBy?: { field: string; direction: "asc" | "desc" } | null,
    filters?: AllFilters,
    keywordSearch?: string,
    missingClaims?: boolean,
    userOnly?: boolean,
  ): Promise<{ claims: Claim[]; total: number; hasMore: boolean }> {
    const endpoint = userOnly
      ? API_CONFIG.ENDPOINTS.CLAIMS_DATA_BY_USER
      : API_CONFIG.ENDPOINTS.CLAIMS_DATA;

    const response = await this.request<{
      claims: Claim[];
      total: number;
      hasMore: boolean;
    }>(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filters: filters,
        page,
        pageSize,
        orderBy: orderBy?.field || "",
        direction: orderBy?.direction || "asc",
        search: keywordSearch || "",
        missingClaims: missingClaims || false,
      }),
    });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Fail to extract claims data");
  }

  // The whole filtered claims queue as CSV, rendered by the backend — same
  // filters/search/missingClaims and sort as getClaimsData, but unpaginated.
  // `columns` (table column ids, in order) limits the file to those columns.
  // POST like getClaimsData: the filters are a nested object.
  async downloadClaimsCsv(params: {
    orderBy?: { field: string; direction: "asc" | "desc" } | null;
    filters?: AllFilters;
    keywordSearch?: string;
    missingClaims?: boolean;
    columns?: string[];
    tz?: string;
  }): Promise<ApiResponse<DocumentDownload>> {
    return this.downloadDocument(API_CONFIG.ENDPOINTS.CLAIMS_DATA_DOWNLOAD, {
      method: "POST",
      headers: { Accept: "*/*", "Content-Type": "application/json" },
      body: JSON.stringify({
        filters: params.filters,
        orderBy: params.orderBy?.field || "",
        direction: params.orderBy?.direction || "asc",
        search: params.keywordSearch || "",
        missingClaims: params.missingClaims || false,
        columns: params.columns,
        tz: params.tz,
      }),
    });
  }

  /** Totals for the whole filtered claim set, not just the current page.
   * Takes the same filters/search/missingClaims as getClaimsData so the tiles
   * describe exactly the rows the table is showing (REV-2507). */
  async getClaimsSummary(
    filters?: AllFilters,
    keywordSearch?: string,
    missingClaims?: boolean,
  ): Promise<ClaimsSummary> {
    const response = await this.request<ClaimsSummary>(
      API_CONFIG.ENDPOINTS.CLAIMS_SUMMARY,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          filters: filters,
          search: keywordSearch || "",
          missingClaims: missingClaims || false,
        }),
      },
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Fail to extract claims summary");
  }

  async getClaimsFilterOptions(): Promise<Record<string, SelectOption[]>> {
    const response = await this.request<Record<string, SelectOption[]>>(
      API_CONFIG.ENDPOINTS.CLAIMS_FILTER_OPTIONS,
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Fail to extract claims filter options");
  }

  async getClaimFilterViews(): Promise<ApiResponse<ClaimFilterView[]>> {
    const response = await this.request<ClaimFilterView[]>(
      API_CONFIG.ENDPOINTS.CLAIMS_FILTER_VIEWS,
    );
    if (response.success && response.data) {
      return response;
    }
    throw new Error(response.error || "Fail to extract claim filter views");
  }

  async deleteClaimFilterView(viewId: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_CONFIG.ENDPOINTS.CLAIM_FILTER_VIEW(viewId), {
      method: "DELETE",
    });
  }

  async createClaimFiltersView(
    name: string,
    filters: SavedClaimViewFilters,
  ): Promise<ApiResponse<ClaimFilterView>> {
    return this.request<ClaimFilterView>(
      API_CONFIG.ENDPOINTS.CREATE_CLAIM_FILTERS_VIEW,
      {
        method: "POST",
        body: JSON.stringify({ name, filters }),
      },
    );
  }

  async updateClaimFiltersView(
    viewId: string,
    name: string,
    filters: SavedClaimViewFilters,
  ): Promise<ApiResponse<ClaimFilterView>> {
    return this.request<ClaimFilterView>(
      API_CONFIG.ENDPOINTS.CLAIM_FILTER_VIEW(viewId),
      {
        method: "PUT",
        body: JSON.stringify({ name, filters }),
      },
    );
  }

  async updateClaimAssignedUser(
    claimId: string,
    assignedUser: string,
  ): Promise<void> {
    const response = await this.request<void>(
      API_CONFIG.ENDPOINTS.CLAIM_UPDATE_ASSIGNED_USER(claimId),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assignedUser }),
      },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to update assigned user");
    }
  }

  async uploadClaimsEdi(
    files: File[],
    batchSize: number = 50,
  ): Promise<ClaimsUploadResult[]> {
    // Create batches
    const batches: File[][] = [];
    for (let i = 0; i < files.length; i += batchSize) {
      batches.push(files.slice(i, i + batchSize));
    }

    const results: ClaimsUploadResult[] = [];
    for (const batch of batches) {
      const result = await this.uploadClaimsEdiBatch(batch);
      results.push(result);
    }

    return results;
  }

  private async uploadClaimsEdiBatch(
    files: File[],
  ): Promise<ClaimsUploadResult> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await this.request<ClaimsUploadResult>(
      API_CONFIG.ENDPOINTS.CLAIMS_UPLOAD,
      {
        method: "POST",
        headers: {}, // Let browser set Content-Type for FormData
        body: formData,
      },
    );

    if (response.success && response.data) {
      return response.data;
    }

    // Handle HTTP 400 responses that contain error details in the response body
    // The backend returns HTTP 400 with a ClaimsUploadResult structure containing an errors array
    if (!response.success && response.status === 400 && response.data) {
      const errorData = response.data as ClaimsUploadResult;

      // If the response contains an errors array, format those errors
      if (
        errorData.errors &&
        Array.isArray(errorData.errors) &&
        errorData.errors.length > 0
      ) {
        const errorMessages = errorData.errors
          .map((e: { file: string; error: string }) => `${e.file}: ${e.error}`)
          .join("; ");
        throw new Error(errorMessages);
      }
    }

    throw new Error(response.error || "Failed to upload EDI files");
  }

  async getClaimDetail(claimId: string): Promise<ClaimDetailResponse> {
    const response = await this.request<ClaimDetailResponse>(
      API_CONFIG.ENDPOINTS.CLAIM_DETAIL(claimId),
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Failed to load claim details");
  }

  /** The FeeChange recording this action would make (REV-2542). Writes nothing.
   * Failures keep the body in `data` ({error, message, details}). */
  async previewClaimAppeal(
    claimId: string,
    input: RecordAppealInput,
  ): Promise<ApiResponse<FeeChange>> {
    return this.request<FeeChange>(
      API_CONFIG.ENDPOINTS.CLAIM_APPEALS_PREVIEW(claimId),
      {
        method: "POST",
        body: JSON.stringify({
          actionOn: input.actionOn,
          actionType: input.actionType,
          // A correct and resubmit has no payer (REV-2628)
          ...(input.actionType !== "correct_resubmit" && {
            payerTier: input.payerTier,
          }),
          timezone: input.timezone,
        }),
      },
    );
  }

  /** Record an action on the claim (REV-2538, REV-2628), with its optional PDF.
   * Add-only. An empty comment gets the backend's default log line. */
  async recordClaimAppeal(
    claimId: string,
    input: RecordAppealInput,
    document: File | null,
  ): Promise<ApiResponse<RecordAppealResponse>> {
    const formData = new FormData();
    formData.append("actionOn", input.actionOn);
    formData.append("actionType", input.actionType);
    if (input.actionType !== "correct_resubmit")
      formData.append("payerTier", input.payerTier);
    formData.append("comment", input.comment);
    formData.append("timezone", input.timezone);
    if (document) formData.append("document", document);

    return this.request<RecordAppealResponse>(
      API_CONFIG.ENDPOINTS.CLAIM_APPEALS(claimId),
      {
        method: "POST",
        headers: {}, // Let browser set Content-Type for FormData
        body: formData,
      },
    );
  }

  /** Admins only: the claims this one can be linked with in `role`, best fit
   * first (REV-2629). A typed number the API can't link is refused with
   * {error, message}: claim_not_found, already_linked, link_cycle... */
  async getClaimLinkCandidates(
    claimId: string,
    query: ClaimLinkCandidatesQuery,
  ): Promise<ApiResponse<ClaimLinkCandidatesResponse>> {
    const params = new URLSearchParams({
      role: query.role,
      sameDateOfService: String(query.sameDateOfService),
    });
    if (query.claimNumber?.trim())
      params.set("claimNumber", query.claimNumber.trim());
    return this.request<ClaimLinkCandidatesResponse>(
      `${API_CONFIG.ENDPOINTS.CLAIM_LINK_CANDIDATES(claimId)}?${params}`,
    );
  }

  /** Admins only: links the claim with its original or corrected claim, writing
   * both claims' log lines (REV-2629). A different patient is refused with
   * different_patient unless confirmed. */
  async linkClaim(
    claimId: string,
    input: LinkClaimInput,
  ): Promise<ApiResponse<{ link: ClaimLink }>> {
    return this.request<{ link: ClaimLink }>(
      API_CONFIG.ENDPOINTS.CLAIM_LINKS(claimId),
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  /** Admins only: what linking would do (REV-2657), with the link call's body.
   * Writes nothing. Refused like linkClaim, except for a different patient. */
  async previewLinkClaim(
    claimId: string,
    input: LinkClaimInput,
  ): Promise<ApiResponse<ClaimLinkPreview>> {
    return this.request<ClaimLinkPreview>(
      API_CONFIG.ENDPOINTS.CLAIM_LINKS_PREVIEW(claimId),
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  /** Admins only: unlinks, from either claim, with a required reason (REV-2629). */
  async unlinkClaim(
    claimId: string,
    linkId: string,
    reason: string,
  ): Promise<ApiResponse<{ link: ClaimLink }>> {
    return this.request<{ link: ClaimLink }>(
      API_CONFIG.ENDPOINTS.CLAIM_LINK_UNLINK(claimId, linkId),
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  }

  /** The FeeChange recording this manual payment would make (REV-2584): the
   * record fields without the document. Writes nothing. Failures keep the body
   * in `data` ({error, message, details}). */
  async previewClaimManualPayment(
    claimId: string,
    input: RecordManualPaymentInput,
  ): Promise<ApiResponse<FeeChange>> {
    const body = Object.fromEntries(
      Object.entries(input).filter(([, value]) => value),
    );
    return this.request<FeeChange>(
      API_CONFIG.ENDPOINTS.CLAIM_MANUAL_PAYMENTS_PREVIEW(claimId),
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  /** Record a payment or denial a payer sent by letter (REV-2582), with its
   * optional correspondence PDF (REV-2593). Admins only; preview it with
   * previewClaimManualPayment. */
  async recordClaimManualPayment(
    claimId: string,
    input: RecordManualPaymentInput,
    document: File | null,
  ): Promise<ApiResponse<RecordManualPaymentResponse>> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(input)) {
      if (value) formData.append(key, value);
    }
    if (document) formData.append("document", document);

    return this.request<RecordManualPaymentResponse>(
      API_CONFIG.ENDPOINTS.CLAIM_MANUAL_PAYMENTS(claimId),
      {
        method: "POST",
        headers: {}, // Let browser set Content-Type for FormData
        body: formData,
      },
    );
  }

  /** Soft-remove a manual payment (REV-2582). `dryRun` runs every check and saves nothing. */
  async removeClaimManualPayment(
    claimId: string,
    attachmentId: string,
    reason: string,
    dryRun: boolean,
  ): Promise<ApiResponse<RemoveManualPaymentResponse>> {
    return this.request<RemoveManualPaymentResponse>(
      API_CONFIG.ENDPOINTS.CLAIM_MANUAL_PAYMENT_REMOVE(claimId, attachmentId),
      {
        method: "POST",
        body: JSON.stringify({ reason, dryRun }),
      },
    );
  }

  /** Join a manual payment with the 835 that duplicates it (REV-2585): the 835
   * becomes the payment of record. Admins only, can't be undone. `dryRun` runs
   * every check and saves nothing. */
  async joinClaimManualPayment(
    claimId: string,
    attachmentId: string,
    remitAttachmentId: string,
    dryRun: boolean,
  ): Promise<ApiResponse<JoinManualPaymentResponse>> {
    return this.request<JoinManualPaymentResponse>(
      API_CONFIG.ENDPOINTS.CLAIM_MANUAL_PAYMENT_JOIN(claimId, attachmentId),
      {
        method: "POST",
        body: JSON.stringify({ remitAttachmentId, dryRun }),
      },
    );
  }

  /** A PDF attached to the claim (an appeal's document or a payment's correspondence). */
  async downloadClaimDocument(
    claimId: string,
    documentId: string,
    inline = false,
  ): Promise<ApiResponse<DocumentDownload>> {
    const path = API_CONFIG.ENDPOINTS.CLAIM_DOCUMENT(claimId, documentId);
    return this.downloadDocument(inline ? `${path}?inline=1` : path);
  }

  async uploadClaimAnalysisFile(
    file: File,
    metadata: ContactSupportFormType & { purpose: string },
    timestamp?: string | null, // TODO:
  ): Promise<{
    success: boolean;
    message?: string;
    timestamp?: string | null;
    ticket?: ITicket;
  }> {
    // Send file in chunks
    const totalChunks = Math.ceil(file.size / UPLOAD_CHUNK_SIZE);
    let lastResponse: ApiResponse<FileUploadResponse> | null = null;

    let uploadRefId = null as null | string;
    let currentTimestamp = timestamp as null | string;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * UPLOAD_CHUNK_SIZE;
      const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
      const chunkBlob = file.slice(start, end);

      const uploadHeaders: Record<string, string> = {
        ...this.headers,
        "Content-Type": "application/octet-stream",
        "x-filename": encodeURIComponent(file.name),
        "x-chunk-index": String(chunkIndex),
        "x-total-chunks": String(totalChunks),
        "x-chunk-size": String(UPLOAD_CHUNK_SIZE),
        "x-total-size": String(file.size),
        "x-purpose": metadata.purpose,
        "x-patient-name": metadata.patientName,
        "x-mrn": metadata.mrn,
        ...(metadata.surgeryDate && { "x-surgery-date": metadata.surgeryDate }),
        ...(metadata.claimNumber && { "x-claim-number": metadata.claimNumber }),
        ...(metadata.comment && { "x-comment": metadata.comment }),
        ...(timestamp && { "x-timestamp": timestamp }),
        ...(metadata?.subject ? { "x-subject": metadata.subject } : {}),
        ...(metadata?.denialType
          ? { "x-denial-type": metadata.denialType }
          : {}),
      };
      if (uploadRefId) uploadHeaders["x-upload-id"] = uploadRefId;

      const response = await this.request<FileUploadResponse>(
        API_CONFIG.ENDPOINTS.CLAIM_ANALYSIS_FILE_CHUNKED_UPLOAD,
        {
          method: "POST",
          headers: uploadHeaders,
          body: chunkBlob,
        },
      );

      if (response?.data?.upload_id) {
        uploadRefId = response?.data?.upload_id;
      }
      if (response?.data?.timestamp) {
        currentTimestamp = response?.data?.timestamp;
      }

      if (!response.success) {
        return {
          success: false,
          message: response.error || "Failed to upload file",
          timestamp: currentTimestamp,
        };
      }

      lastResponse = response;
    }

    return lastResponse
      ? {
          success: true,
          timestamp: currentTimestamp,
          ticket: lastResponse.data?.ticket,
        }
      : {
          success: false,
          message: "No chunks were uploaded",
          timestamp: currentTimestamp,
        };
  }

  async uploadClaimAnalysisFiles(
    files: File[],
    metadata: ContactSupportFormType & { purpose: string },
    timestamp?: string | null,
  ): Promise<{
    success: boolean;
    message?: string;
    timestamp?: string;
    ticket?: ITicket;
  }> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    formData.append("purpose", metadata.purpose);
    if (metadata.patientName)
      formData.append("patientName", metadata.patientName);
    if (metadata.mrn) formData.append("mrn", metadata.mrn);
    if (metadata.comment) formData.append("comment", metadata.comment);
    if (metadata.surgeryDate)
      formData.append("surgeryDate", metadata.surgeryDate);
    if (metadata.claimNumber)
      formData.append("claimNumber", metadata.claimNumber);
    if (timestamp) formData.append("timestamp", timestamp);
    if (metadata.subject) formData.append("subject", metadata.subject);
    if (metadata.denialType) formData.append("denialType", metadata.denialType);

    const response = await this.request<{ timestamp?: string; ticket?: ITicket }>(
      API_CONFIG.ENDPOINTS.CLAIM_ANALYSIS_FILES_UPLOAD,
      {
        method: "POST",
        headers: {}, // Let browser set Content-Type for FormData
        body: formData,
      },
    );

    if (response.success && response.data) {
      return {
        success: true,
        timestamp: response.data.timestamp,
        ticket: response.data.ticket,
      };
    }

    // Handle HTTP 400 responses that contain error details in the response body
    // The backend returns HTTP 400 with a ClaimsUploadResult structure containing an errors array
    if (!response.success && response.status === 400 && response.data) {
      const errorData = response.data as ClaimsUploadResult;

      // If the response contains an errors array, format those errors
      if (
        errorData.errors &&
        Array.isArray(errorData.errors) &&
        errorData.errors.length > 0
      ) {
        const errorMessages = errorData.errors
          .map((e: { file: string; error: string }) => `${e.file}: ${e.error}`)
          .join("; ");

        return {
          success: false,
          message: errorMessages,
          timestamp: response?.data?.timestamp,
        };
      }
    }

    return {
      success: false,
      message: response.error || "Failed to upload files",
      timestamp: response.data?.timestamp,
    };
  }

  // Upload History Dashboard
  async getUploadHistoryDaily(
    startDate: string,
    endDate: string,
    status?: string,
    ediObjectType?: string,
  ): Promise<DailyStatsResponse> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (status) params.append("status", status);
    if (ediObjectType) params.append("edi_object_type", ediObjectType);

    const response = await this.request<DailyStatsResponse>(
      `${API_CONFIG.ENDPOINTS.UPLOAD_HISTORY_DAILY}?${params}`,
      { method: "GET" },
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || "Failed to load daily statistics");
  }

  async getUploadHistoryFiles(params: {
    date?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    edi_object_type?: string;
    file_name?: string;
    claim_number?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    order?: "asc" | "desc";
  }): Promise<FileListResponse> {
    const queryParams = new URLSearchParams();
    if (params.date) queryParams.append("date", params.date);
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);
    if (params.status) queryParams.append("status", params.status);
    if (params.edi_object_type)
      queryParams.append("edi_object_type", params.edi_object_type);
    if (params.file_name) queryParams.append("file_name", params.file_name);
    if (params.claim_number)
      queryParams.append("claim_number", params.claim_number);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.page_size)
      queryParams.append("page_size", params.page_size.toString());
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.order) queryParams.append("order", params.order);

    const response = await this.request<FileListResponse>(
      `${API_CONFIG.ENDPOINTS.UPLOAD_HISTORY_FILES}?${queryParams}`,
      { method: "GET" },
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || "Failed to load file list");
  }

  // The whole filtered upload history as CSV, rendered by the backend — same
  // filters and sort as getUploadHistoryFiles, but unpaginated. `columns`
  // (table column ids, in order) limits the file to those columns.
  async downloadUploadHistoryCsv(params: {
    start_date?: string;
    end_date?: string;
    status?: string;
    edi_object_type?: string;
    file_name?: string;
    claim_number?: string;
    sort_by?: string;
    order?: "asc" | "desc";
    columns?: string[];
    tz?: string;
  }): Promise<ApiResponse<DocumentDownload>> {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append("start_date", params.start_date);
    if (params.end_date) queryParams.append("end_date", params.end_date);
    if (params.status) queryParams.append("status", params.status);
    if (params.edi_object_type)
      queryParams.append("edi_object_type", params.edi_object_type);
    if (params.file_name) queryParams.append("file_name", params.file_name);
    if (params.claim_number)
      queryParams.append("claim_number", params.claim_number);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.order) queryParams.append("order", params.order);
    params.columns?.forEach((column) => queryParams.append("columns", column));
    if (params.tz) queryParams.append("tz", params.tz);

    return this.downloadDocument(
      `${API_CONFIG.ENDPOINTS.UPLOAD_HISTORY_FILES_DOWNLOAD}?${queryParams}`,
    );
  }

  async getUploadHistoryFileDetail(fileId: string): Promise<FileHistoryDetail> {
    const response = await this.request<FileHistoryDetail>(
      API_CONFIG.ENDPOINTS.UPLOAD_HISTORY_FILE_DETAIL(fileId),
      { method: "GET" },
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || "Failed to load file details");
  }

  async getReleaseVersions(): Promise<ReleaseVersionsResponse> {
    const response = await this.request<ReleaseVersionsResponse>(
      API_CONFIG.ENDPOINTS.RELEASE_VERSIONS,
      { method: "GET" },
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || "Failed to load release versions");
  }

  async reprocessFailedFiles(
    fileIds: string[],
  ): Promise<ReprocessFilesResponse> {
    const response = await this.request<ReprocessFilesResponse>(
      API_CONFIG.ENDPOINTS.UPLOAD_HISTORY_REPROCESS,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_ids: fileIds }),
      },
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || "Failed to reprocess files");
  }

  async setChatMessageFeedback(
    chatId: string,
    messageId: string,
    action: "up" | "down",
    isClearFeedback: boolean = false,
  ): Promise<ApiResponse<void>> {
    return this.request<void>(
      API_CONFIG.ENDPOINTS.CHAT_MESSAGE_FEEDBACK(chatId, messageId),
      {
        method: isClearFeedback ? "DELETE" : "POST",
        body: JSON.stringify({ rating: action, comment: "" }),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  }

  async updateChatShared(
    chatId: string,
    shared: boolean,
  ): Promise<ApiResponse<ChatSession>> {
    return this.request<ChatSession>(`/chats/${chatId}/`, {
      method: "PATCH",
      body: JSON.stringify({ shared }),
    });
  }

  //#region Observability
  private appendObservabilityFilters(
    queryParams: URLSearchParams,
    filters?: Partial<
      ObservabilityDateRange & { bucket: string; limit: number }
    >,
  ): void {
    if (filters?.from) queryParams.append("from", filters.from);
    if (filters?.to) queryParams.append("to", filters.to);

    if (filters?.bucket) queryParams.append("bucket", filters.bucket);
    if (filters?.limit !== null && typeof filters?.limit !== "undefined") {
      queryParams.append("limit", String(filters.limit));
    }
  }

  private appendUsageActivityFeedColumnFilters(
    queryParams: URLSearchParams,
    filters?: UsageActivityFeedColumnFilters | null,
  ): void {
    if (!filters) return;

    for (const [columnId, raw] of Object.entries(filters)) {
      if (!raw) continue;

      if (Array.isArray(raw)) {
        raw.forEach((value) => {
          if (value) queryParams.append(columnId, value);
        });
        continue;
      }

      const entry = raw as ColumnFilterEntry;
      if (entry.operator && entry.operator !== "and") {
        queryParams.append(`${columnId}_op`, entry.operator);
      }
      entry.values?.forEach((value) => {
        if (value != null && value !== "") {
          queryParams.append(columnId, String(value));
        }
      });
    }
  }

  // Unlike appendUsageActivityFeedColumnFilters, values keep their position,
  // empty ones included: range filters are [min, max], so a max-only filter
  // must go out as `paid_now=&paid_now=500`. A filter with no non-empty value
  // is skipped.
  private appendSuccessFeesPerClaimColumnFilters(
    queryParams: URLSearchParams,
    filters?: Record<string, ColumnFilterState> | null,
  ): void {
    if (!filters) return;

    for (const [columnId, filter] of Object.entries(filters)) {
      const values = (filter?.values ?? []).map((value) =>
        value == null ? "" : String(value),
      );
      if (!values.some((value) => value !== "")) continue;

      if (filter.operator && filter.operator !== "and") {
        queryParams.append(`${columnId}_op`, filter.operator);
      }
      values.forEach((value) => queryParams.append(columnId, value));
    }
  }

  private observabilityEndpoint(
    base: string,
    queryParams: URLSearchParams,
  ): string {
    const queryString = queryParams.toString();
    return queryString ? `${base}?${queryString}` : base;
  }

  async getUsageByAgent(
    dateRange?: ObservabilityDateRange,
  ): Promise<ApiResponse<{ rows: UsageByAgent[] }>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, dateRange);

    return this.request<{ rows: UsageByAgent[] }>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.USAGE_BY_AGENT,
        queryParams,
      ),
    );
  }

  async getUsageCasesBreakdown(
    dateRange?: ObservabilityDateRange,
  ): Promise<ApiResponse<CasesBreakdown>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, dateRange);

    return this.request<CasesBreakdown>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.USAGE_CASES_BREAKDOWN,
        queryParams,
      ),
    );
  }

  async getUsageKpis(
    filters?: ObservabilityDateRange,
  ): Promise<ApiResponse<UsageKpis>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, filters);

    return this.request<UsageKpis>(
      this.observabilityEndpoint(API_CONFIG.ENDPOINTS.USAGE_KPIS, queryParams),
    );
  }

  async getClaimOutcomeTracking(
    filters?: ObservabilityDateRange,
  ): Promise<ApiResponse<ClaimOutcomeTracking>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, filters);

    return this.request<ClaimOutcomeTracking>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.CLAIM_OUTCOME_TRACKING,
        queryParams,
      ),
    );
  }

  async getUsageActivityFeed(
    params?: Partial<ObservabilityDateRange> & {
      limit?: number;
      offset?: number;
      sort?: string;
      order?: "asc" | "desc";
      columnFilters?: UsageActivityFeedColumnFilters | null;
    },
  ): Promise<ApiResponse<UsageActivityFeedResponse>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, params);
    if (params?.offset !== undefined && params.offset !== null) {
      queryParams.append("offset", String(params.offset));
    }
    if (params?.sort) {
      queryParams.append("sort", params.sort);
      queryParams.append("order", params.order ?? "desc");
    }
    this.appendUsageActivityFeedColumnFilters(
      queryParams,
      params?.columnFilters,
    );

    return this.request<UsageActivityFeedResponse>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.USAGE_ACTIVITY_FEED,
        queryParams,
      ),
    );
  }

  // The whole filtered feed as CSV, rendered by the backend — same date range,
  // sort and column filters as getUsageActivityFeed, but unpaginated. `columns`
  // (table column ids, in order) limits the file to those columns.
  async downloadUsageActivityFeedCsv(
    params?: Partial<ObservabilityDateRange> & {
      sort?: string;
      order?: "asc" | "desc";
      columnFilters?: UsageActivityFeedColumnFilters | null;
      columns?: string[];
      tz?: string;
    },
  ): Promise<ApiResponse<DocumentDownload>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, params);
    if (params?.sort) {
      queryParams.append("sort", params.sort);
      queryParams.append("order", params.order ?? "desc");
    }
    this.appendUsageActivityFeedColumnFilters(
      queryParams,
      params?.columnFilters,
    );
    params?.columns?.forEach((column) => queryParams.append("columns", column));
    if (params?.tz) queryParams.append("tz", params.tz);

    return this.downloadDocument(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.USAGE_ACTIVITY_FEED_DOWNLOAD,
        queryParams,
      ),
    );
  }

  // Distinct values for the feed's list-filter columns across ALL feed data
  // (no date range / column filters), keyed by column id.
  async getUsageActivityFeedOptions(): Promise<
    ApiResponse<Record<string, SelectOption[]>>
  > {
    return this.request<Record<string, SelectOption[]>>(
      API_CONFIG.ENDPOINTS.USAGE_ACTIVITY_FEED_OPTIONS,
    );
  }

  async getFinancialImpact(
    filters?: ObservabilityDateRange,
  ): Promise<ApiResponse<FinancialImpact>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, filters);

    return this.request<FinancialImpact>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.FINANCIAL_IMPACT,
        queryParams,
      ),
    );
  }

  // One row per claim with a billable appeal — success fee computed once per
  // claim on its canonical recovery chain (REV-1824).
  async getSuccessFeesPerClaim(
    filters?: ObservabilityDateRange,
  ): Promise<ApiResponse<SuccessFeesPerClaimResponse>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, filters);

    return this.request<SuccessFeesPerClaimResponse>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.SUCCESS_FEES_PER_CLAIM,
        queryParams,
      ),
    );
  }

  // The per-claim success fee report as CSV, rendered by the backend for the
  // same date range as getSuccessFeesPerClaim, plus the table's column filters
  // and sort (applied client-side in the table, server-side here). `columns`
  // (table column ids, in order) limits the file to those columns.
  async downloadSuccessFeesPerClaimCsv(
    params?: Partial<ObservabilityDateRange> & {
      sort?: string;
      order?: "asc" | "desc";
      columnFilters?: Record<string, ColumnFilterState> | null;
      columns?: string[];
      tz?: string;
    },
  ): Promise<ApiResponse<DocumentDownload>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, params);
    if (params?.sort) {
      queryParams.append("sort", params.sort);
      queryParams.append("order", params.order ?? "desc");
    }
    this.appendSuccessFeesPerClaimColumnFilters(
      queryParams,
      params?.columnFilters,
    );
    params?.columns?.forEach((column) => queryParams.append("columns", column));
    if (params?.tz) queryParams.append("tz", params.tz);

    return this.downloadDocument(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.SUCCESS_FEES_PER_CLAIM_DOWNLOAD,
        queryParams,
      ),
    );
  }

  // Distinct values for the success-fees report's list-filter columns across
  // ALL report data (no date range / column filters), keyed by column id.
  async getSuccessFeesPerClaimOptions(): Promise<
    ApiResponse<Record<string, SelectOption[]>>
  > {
    return this.request<Record<string, SelectOption[]>>(
      API_CONFIG.ENDPOINTS.SUCCESS_FEES_PER_CLAIM_OPTIONS,
    );
  }

  async getDrillDownCapabilityLineItems(
    filters?: ObservabilityDateRange,
  ): Promise<ApiResponse<{ rows: DrillDownCapabilityLineItemRow[] }>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, filters);

    return this.request<{ rows: DrillDownCapabilityLineItemRow[] }>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.DRILL_DOWN_CAPABILITY_LINE_ITEMS,
        queryParams,
      ),
    );
  }

  async getDrillDownCapabilityCompute(
    filters?: ObservabilityDateRange,
  ): Promise<ApiResponse<DrillDownCapability>> {
    const queryParams = new URLSearchParams();
    this.appendObservabilityFilters(queryParams, filters);

    return this.request<DrillDownCapability>(
      this.observabilityEndpoint(
        API_CONFIG.ENDPOINTS.DRILL_DOWN_CAPABILITY_COMPUTE,
        queryParams,
      ),
    );
  }
  //#endregion

  //#region Tickets

  async listTickets(params: {
    page?: number;
    per_page?: number;
    state?: string;
    keywordSearch?: string;
    columnFilters?: TicketColumnFilters | null;
    sort_by?: string;
    sort_direction?: string;
  }): Promise<ApiResponse<ITicket[] | ITicketListResponse>> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", String(params.page));
    if (params.per_page)
      queryParams.append("per_page", String(params.per_page));
    if (params.state) queryParams.append("state", params.state);
    if (params.keywordSearch)
      queryParams.append("search", params.keywordSearch);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.sort_direction)
      queryParams.append("sort_direction", params.sort_direction);
    this.appendTicketColumnFilters(queryParams, params.columnFilters);
    const qs = queryParams.toString();
    return this.request<ITicket[] | ITicketListResponse>(
      `${API_CONFIG.ENDPOINTS.TICKETS}${qs ? `?${qs}` : ""}`,
    );
  }

  /** Mirrors appendUsageActivityFeedColumnFilters: no dedicated ticket filter contract
   * exists yet, so this is the same columnId(_op)=value convention, unverified. */
  private appendTicketColumnFilters(
    queryParams: URLSearchParams,
    filters?: TicketColumnFilters | null,
  ): void {
    if (!filters) return;

    for (const [columnId, raw] of Object.entries(filters)) {
      if (!raw) continue;

      if (Array.isArray(raw)) {
        raw.forEach((value) => {
          if (value) queryParams.append(columnId, value);
        });
        continue;
      }

      const entry = raw as ColumnFilterEntry;
      if (entry.operator && entry.operator !== "and") {
        queryParams.append(`${columnId}_op`, entry.operator);
      }
      entry.values?.forEach((value) => {
        if (value != null && value !== "") {
          queryParams.append(columnId, String(value));
        }
      });
    }
  }

  async getTicket(id: number): Promise<ApiResponse<ITicketDetail>> {
    return this.request<ITicketDetail>(API_CONFIG.ENDPOINTS.TICKET_DETAIL(id));
  }

  // Files are uploaded through uploadFile() first; the reply carries their ids.
  async createTicketArticle(
    ticketId: number,
    data: { body: string; fileIds?: string[] },
  ): Promise<ApiResponse<unknown>> {
    const payload: { body: string; file_ids?: string[] } = { body: data.body };
    if (data.fileIds && data.fileIds.length > 0) {
      payload.file_ids = data.fileIds;
    }

    return this.request(API_CONFIG.ENDPOINTS.TICKET_ARTICLES(ticketId), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getTicketAttachmentUrl(
    ticketId: number,
    articleId: number,
    attachmentId: number,
  ): string {
    return `${this.baseUrl}${API_CONFIG.ENDPOINTS.TICKET_ATTACHMENT(ticketId, articleId, attachmentId)}`;
  }

  /**
   * Fetch a ticket file. `downloadPath` is either an endpoint relative to the API
   * base or a backend `download_url` (`/api/v1/files/stream/?...`); the latter
   * already carries the `/api/v1` that this.baseUrl ends in, so it is stripped.
   */
  async downloadTicketFile(
    downloadPath: string,
  ): Promise<ApiResponse<DocumentDownload>> {
    const apiPrefix = `/api/${API_CONFIG.VERSION || "v1"}/`;
    const endpoint = downloadPath.startsWith(apiPrefix)
      ? downloadPath.slice(apiPrefix.length - 1)
      : downloadPath;

    return this.downloadDocument(endpoint);
  }

  async reopenTicket(ticketId: number): Promise<ApiResponse<ITicket>> {
    return this.request<ITicket>(API_CONFIG.ENDPOINTS.TICKET_REOPEN(ticketId), {
      method: "POST",
    });
  }

  //#endregion
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export the class for custom instances
export default ApiService;
